import React, { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useForm, Controller } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Col } from "reactstrap";
import Swal from "sweetalert2";

import { UPDATE_JOB_DESCRIPTION } from "@/GraphQL/Mutation";
import { GET_USER_BY_USERID } from "@/GraphQL/Queries";
import CkEditorInput from "../Company/components/Notice/CkEditorInput";
import Loader from "../Account/Profile/components/Loader";
import SubmitLoading from "@/utils/HelperComponent/SubmitLoading";

export default function JobDescription({ match }) {
  const userId = match?.params?.id;
  const history = useHistory();

  const { data, loading: queryLoading } = useQuery(GET_USER_BY_USERID, {
    skip: !userId,
    variables: {
      id: Number(userId),
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [updateJobDescription, { loading: mutationLoading, error }] =
    useMutation(UPDATE_JOB_DESCRIPTION);
  const applyJobDescription = (description) => {
    if (description) {
      setValue("description", description);
    }
  };

  // ✅ Load and set default value once query completes
  useEffect(() => {
    applyJobDescription(data?.getUserByUserid?.job_description);
  }, [data?.getUserByUserid?.job_description, setValue]);

  // ✅ Submit handler
  const onSubmit = async (formData) => {
    try {
      await updateJobDescription({
        variables: {
          id: Number(userId),
          job_description: formData.description,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "✅ Job description updated successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      history.goBack();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Oops!",
        text: "❌ Failed to update: " + err.message,
      });
    }
  };

  // ✅ Cancel handler
  const handleCancel = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Discard changes and go back?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, discard",
      cancelButtonText: "Stay",
    }).then((result) => {
      if (result.isConfirmed) {
        history.goBack();
      }
    });
  };

  if (queryLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-80">
        <SubmitLoading color="black" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-4 card shadow-sm mt-4">
      <h4 className="fw-bold mb-3">✍️ Update Job Description</h4>

      <Col md={12}>
        <div className="form__form-group">
          <label className="form__form-group-label">Description</label>
          <div>
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field: { onChange, value } }) => (
                <CkEditorInput
                  onChange={onChange}
                  value={value}
                  height={400}
                  placeholder={"Enter job description here..."}
                />
              )}
            />
            {errors.description && (
              <span className="text-danger mt-1 d-block">
                {errors.description.message}
              </span>
            )}
          </div>
        </div>
      </Col>

      <div className="d-flex justify-content-end gap-2">
        <button className="btn btn-danger" type="button" onClick={handleCancel}>
          Cancel
        </button>

        <button
          className="btn btn-primary"
          type="submit"
          disabled={mutationLoading}
        >
          {mutationLoading ? "Updating..." : "Update"}
        </button>
      </div>

      {error && <div className="text-danger mt-2">{error.message}</div>}
    </form>
  );
}
